import React, { useEffect, useState } from "react";
import { Calendar, Database } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

// Discord Icon Component
const DiscordIcon = () => (
  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

type MortgageChainFormType = "call" | "deck";

const mortgageNavItems = [
  { id: "problem", label: "Problem" },
  { id: "solution", label: "Features" },
  { id: "how-it-works", label: "How It Works" },
  { id: "use-cases", label: "Use Cases" },
  { id: "technology", label: "Technology" },
  // { id: "pricing", label: "Pricing" },
  { id: "api", label: "API Docs" },
] as const;

const docsNavItems = [
  { id: "docs-overview", label: "Overview" },
  { id: "docs-quickstart", label: "Quick Start" },
  { id: "docs-authentication", label: "Authentication" },
  { id: "docs-endpoints", label: "Endpoints" },
  { id: "docs-errors", label: "Errors" },
  { id: "docs-rate-limits", label: "Rate Limits" },
  { id: "docs-webhooks", label: "Webhooks" },
] as const;

export default function StickyNavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const isPitchDeck = location.pathname === "/pitch-deck";

  const isMortgageChainDocs =
    location.pathname === "/docs" ||
    location.pathname === "/mortgagechain/docs" ||
    location.pathname.startsWith("/mortgagechain/docs/") ||
    location.pathname.startsWith("/docs/");
  const isLegalPage =
    location.pathname === "/privacy-policy" ||
    location.pathname === "/terms-of-service" ||
    location.pathname === "/risk-disclosures" ||
    location.pathname === "/mortgagechain/privacy-policy" ||
    location.pathname === "/mortgagechain/terms-of-service" ||
    location.pathname === "/mortgagechain/risk-disclosures";
  const isMortgageChain =
    !isMortgageChainDocs &&
    (location.pathname === "/" ||
      location.pathname === "/mortgagechain" ||
      location.pathname === "/privacy-policy" ||
      location.pathname === "/terms-of-service" ||
      location.pathname === "/risk-disclosures" ||
      location.pathname.startsWith("/mortgagechain/"));

  const [activeSection, setActiveSection] = useState("hero");

  useEffect(() => {
    setActiveSection(isMortgageChainDocs ? "docs-hero" : "hero");
  }, [isMortgageChain, isMortgageChainDocs]);

  useEffect(() => {
    const isMortgageSection = isMortgageChain || isMortgageChainDocs;
    if (!isMortgageSection) {
      return undefined;
    }

    const handleScroll = () => {
      const sections = isMortgageChain
        ? ["hero", ...mortgageNavItems.map((item) => item.id)]
        : ["docs-hero", ...docsNavItems.map((item) => item.id)];

      const currentSection = sections.find((sectionId) => {
        const element = document.getElementById(sectionId);
        if (!element) {
          return false;
        }
        const rect = element.getBoundingClientRect();
        return rect.top <= 100 && rect.bottom >= 100;
      });

      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isMortgageChain, isMortgageChainDocs]);

  if (isPitchDeck) {
    return null;
  }

  const isStocksPage = location.pathname === "/stocks";

  const handleLogoClick = () => {
    navigate("/");
  };

  const handleDiscordClick = () => {
    window.open("https://discord.gg/nu83bq9G", "_blank");
  };

  const handleSectionClick =
    (sectionId: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      const target = document.getElementById(sectionId);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

  const emitMortgageChainFormEvent = (type: MortgageChainFormType) => {
    window.dispatchEvent(
      new CustomEvent("mortgagechain:openForm", { detail: { type } })
    );
  };

  const handleMortgageScrollToTop = () => {
    setActiveSection("hero");
    if (location.pathname !== "/" && location.pathname !== "/mortgagechain") {
      navigate("/");
    }
    // Scroll to top and hero section
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => {
      const heroElement = document.getElementById("hero");
      if (heroElement) {
        heroElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  /* /stocks: only logo, no menu or Schedule Call */
  if (isStocksPage) {
    return (
      <nav
        className="fixed top-0 left-0 right-0 z-50 border-b px-6 py-3 transition-all duration-300"
        style={{
          background: "rgba(10, 14, 39, 0.95)",
          backdropFilter: "blur(30px)",
          borderBottomColor: "rgba(0, 212, 255, 0.3)",
        }}
      >
        <div className="max-w-7xl mx-auto flex items-center w-full">
          <button
            type="button"
            className="flex items-center gap-3 cursor-pointer px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/50 flex-shrink-0"
            onClick={handleLogoClick}
          >
            <span className="text-xl font-bold logoCrypsey text-white">
              Crypsey
            </span>
          </button>
        </div>
      </nav>
    );
  }

  if (isMortgageChain || isMortgageChainDocs) {
    const navItems = isMortgageChain ? mortgageNavItems : docsNavItems;

    return (
      <nav
        className="fixed top-0 left-0 right-0 z-50 border-b px-6 py-3 transition-all duration-300"
        style={{
          background: "rgba(10, 14, 39, 0.95)",
          backdropFilter: "blur(30px)",
          borderBottomColor: "rgba(0, 212, 255, 0.3)",
        }}
      >
        <div className="max-w-7xl mx-auto flex items-center gap-4 md:gap-6 w-full">
          <button
            type="button"
            className="flex items-center gap-3 cursor-pointer px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/50 flex-shrink-0"
            onClick={handleMortgageScrollToTop}
          >
            <span className="text-xl font-bold logoCrypsey text-white">
              Crypsey
            </span>
          </button>

          {!isLegalPage && (
            <div className="flex items-center gap-4 md:gap-6 lg:gap-8 flex-1 justify-center overflow-x-auto">
              {navItems.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={handleSectionClick(item.id)}
                  className={`text-sm font-medium transition-all duration-300 relative whitespace-nowrap px-1 md:px-2 ${
                    activeSection === item.id
                      ? "text-cyan-400"
                      : "text-gray-300 hover:text-cyan-400"
                  }`}
                  style={{
                    color: activeSection === item.id ? "#22d3ee" : "#d1d5db",
                  }}
                >
                  {item.label}
                  {activeSection === item.id && (
                    <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-500 to-purple-500" />
                  )}
                </a>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={() => emitMortgageChainFormEvent("call")}
              className="btn-font bg-gradient-to-r from-cyan-500 to-purple-500 px-4 md:px-6 py-2 rounded-lg font-semibold hover:shadow-lg hover:shadow-cyan-500/50 transition-all hover:scale-105 flex items-center gap-2 text-sm md:text-base"
            >
              <Calendar className="w-4 h-4" />
              <span className="hidden sm:inline">Schedule Call</span>
              <span className="sm:hidden">Call</span>
            </button>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="bg-white/80 backdrop-blur-sm border-b border-gray-100/50 px-6 py-2 sticky top-0 z-50">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div>
            <h1
              className="logoCrypsey font-semibold m-0"
              onClick={handleLogoClick}
              style={{
                background: "linear-gradient(to right, #9333ea, #2563eb)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                textRendering: "optimizeLegibility",
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
              }}
            >
              Crypsey
            </h1>
          </div>
        </div>
        <div className="flex items-center">
          <button
            onClick={handleDiscordClick}
            className="flex items-center space-x-2 bg-gradient-to-r from-[#5865f2] to-[#4752c4] hover:from-[#4752c4] hover:to-[#3c45a5] text-white px-3 py-1.5 rounded-lg transition-all duration-200"
          >
            <DiscordIcon />
            <span className="text-xs font-medium">Discord</span>
          </button>
        </div>
      </div>
    </nav>
  );
}
